/**
 * 主题管理 Composable - 单例模式
 * 提供统一的主题切换和管理功能
 *
 * @module useTheme
 * @description 全局单例主题管理，支持系统主题检测、本地存储持久化、平滑过渡动画
 *
 * @example
 * ```typescript
 * const { isDarkTheme, currentTheme, toggleTheme, setTheme } = useTheme({
 *   enableSystemDetection: true,
 *   enableTransition: true
 * });
 *
 * // 切换主题
 * toggleTheme();
 *
 * // 设置指定主题
 * setTheme('dark');
 *
 * // 检测系统主题
 * const systemTheme = detectSystemTheme();
 * ```
 */

import { ref, computed, onMounted, onBeforeUnmount, type Ref, type ComputedRef } from 'vue';
import { ElMessage } from 'element-plus';

// 全局单例状态
const isDarkTheme = ref(false);
let isInitialized = false;

export interface ThemeOptions {
  enableSystemDetection?: boolean;
  enableTransition?: boolean;
  storageKey?: string;
}

export interface UseThemeReturn {
  // 状态
  isDarkTheme: Ref<boolean>;
  currentTheme: ComputedRef<'light' | 'dark'>;
  
  // 方法
  toggleTheme: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  detectSystemTheme: () => 'light' | 'dark';
  
  // 工具方法
  applyTheme: (theme: 'light' | 'dark') => void;
  saveThemePreference: (theme: 'light' | 'dark') => void;
  loadThemePreference: () => 'light' | 'dark' | null;
}

// 计算属性
const currentTheme = computed(() => isDarkTheme.value ? 'dark' : 'light');

/**
 * 主题管理 Hook - 单例模式
 *
 * @param {ThemeOptions} options - 主题配置选项
 * @param {boolean} options.enableSystemDetection - 是否启用系统主题检测，默认true
 * @param {boolean} options.enableTransition - 是否启用主题切换过渡动画，默认true
 * @param {string} options.storageKey - 本地存储键名，默认'theme'
 *
 * @returns {UseThemeReturn} 主题管理状态和方法
 */
export function useTheme(options: ThemeOptions = {}): UseThemeReturn {
  const {
    enableSystemDetection = true,
    enableTransition = true,
    storageKey = 'theme'
  } = options;

  /**
   * 检测系统主题偏好
   */
  const detectSystemTheme = (): 'light' | 'dark' => {
    if (typeof window === 'undefined') return 'light';
    
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  };

  /**
   * 应用主题到DOM
   */
  const applyTheme = (theme: 'light' | 'dark') => {
    if (typeof document === 'undefined') return;

    // 添加过渡效果
    if (enableTransition) {
      document.documentElement.style.transition = 'background-color 0.3s ease, color 0.3s ease';
    }

    // 应用主题类
    document.documentElement.classList.toggle('dark', theme === 'dark');
    
    // 移除过渡效果
    if (enableTransition) {
      setTimeout(() => {
        document.documentElement.style.transition = '';
      }, 300);
    }
  };

  /**
   * 保存主题偏好到本地存储
   */
  const saveThemePreference = (theme: 'light' | 'dark') => {
    if (typeof localStorage === 'undefined') return;
    
    try {
      localStorage.setItem(storageKey, theme);
    } catch (error) {
      console.warn('无法保存主题偏好:', error);
    }
  };

  /**
   * 从本地存储加载主题偏好
   */
  const loadThemePreference = (): 'light' | 'dark' | null => {
    if (typeof localStorage === 'undefined') return null;
    
    try {
      const saved = localStorage.getItem(storageKey);
      return saved === 'dark' || saved === 'light' ? saved : null;
    } catch (error) {
      console.warn('无法加载主题偏好:', error);
      return null;
    }
  };

  /**
   * 设置主题
   */
  const setTheme = (theme: 'light' | 'dark') => {
    isDarkTheme.value = theme === 'dark';
    applyTheme(theme);
    saveThemePreference(theme);
  };

  /**
   * 切换主题
   */
  const toggleTheme = () => {
    const newTheme = isDarkTheme.value ? 'light' : 'dark';
    setTheme(newTheme);
    
    ElMessage({
      message: `已切换到${newTheme === 'dark' ? '深色' : '浅色'}主题`,
      type: 'success',
      duration: 1500
    });
  };

  /**
   * 监听系统主题变化
   */
  let mediaQuery: MediaQueryList | null = null;
  const handleSystemThemeChange = (e: MediaQueryListEvent) => {
    if (enableSystemDetection) {
      const systemTheme = e.matches ? 'dark' : 'light';
      setTheme(systemTheme);
    }
  };

  /**
   * 初始化主题
   */
  const initTheme = () => {
    // 优先使用保存的偏好
    const savedTheme = loadThemePreference();
    
    if (savedTheme) {
      setTheme(savedTheme);
    } else if (enableSystemDetection) {
      // 使用系统主题偏好
      const systemTheme = detectSystemTheme();
      setTheme(systemTheme);
    } else {
      // 默认浅色主题
      setTheme('light');
    }
  };

  // 生命周期
  onMounted(() => {
    initTheme();
    
    // 监听系统主题变化
    if (enableSystemDetection && typeof window !== 'undefined') {
      mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addEventListener('change', handleSystemThemeChange);
    }
  });

  onBeforeUnmount(() => {
    // 清理事件监听器
    if (mediaQuery) {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    }
  });

  // 确保只初始化一次
  if (!isInitialized) {
    // 初始化主题
    const savedTheme = loadThemePreference();
    if (savedTheme) {
      setTheme(savedTheme);
    } else if (enableSystemDetection) {
      setTheme(detectSystemTheme());
    }

    // 监听系统主题变化
    if (enableSystemDetection && typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        if (!loadThemePreference()) { // 只有在用户没有手动设置时才跟随系统
          setTheme(e.matches ? 'dark' : 'light');
        }
      };
      mediaQuery.addEventListener('change', handleChange);
    }

    isInitialized = true;
  }

  return {
    // 状态 - ref 本身就是响应式的，不需要 readonly 包装
    isDarkTheme,
    currentTheme,

    // 方法
    toggleTheme,
    setTheme,
    detectSystemTheme,

    // 工具方法
    applyTheme,
    saveThemePreference,
    loadThemePreference
  };
}
